import { ArrowRightIcon, PhoneIcon, MailIcon } from "@heroicons/react/solid";
import { useState } from "react";
import DSGStore from "../DSGStore";

const InputField = (props: any) => {
	return (
		<input
			value={props.value}
			onChange={(e) => props.onChange(e.target.value)}
			className="min-h-[45px] w-full rounded-xl border-none bg-ds-gray-light p-3 pl-4 shadow-sm transition duration-500 focus:border-ds-yellow focus:ring-ds-yellow"
			placeholder={props.placeHolder}
			type={props.type}
		/>
	);
};

const ContactInfo = ({
	location,
	address,
	person,
	phone,
	email,
}: {
	location: string;
	address: string[];
	person: string;
	phone: string;
	email: string;
}) => (
	<div className="mb-8 flex flex-col items-center">
		<h2 className="mt-6 text-lg text-ds-yellow">{location}</h2>
		<div className="mb-4 text-center">
			{address.map((line, i) => (
				<div key={i}>{line}</div>
			))}
		</div>
		<div className="text-center">
			<div className="mb-1 text-sm text-ds-yellow">Ansprechpartner</div>
			{person}
			<br />
			<div className="mt-1 flex items-center justify-center gap-1.5">
				<PhoneIcon className="h-3.5 w-3.5 flex-shrink-0 text-ds-yellow" />
				<a
					href={`tel:${phone.replace(/\s/g, "")}`}
					className="pr-4 transition-colors hover:text-ds-yellow"
				>
					{phone}
				</a>
			</div>
			<div className="mt-1 inline-flex items-center justify-center gap-1.5">
				<MailIcon className="h-3.5 w-3.5 flex-shrink-0 text-ds-yellow" />
				<a
					href={`mailto:${email}`}
					className="whitespace-nowrap text-ds-yellow"
				>
					{email}
				</a>
			</div>
		</div>
	</div>
);

const Contact = () => {
	const [sending, setSending] = useState(false);

	const [name, setName] = useState("");
	const [email, setEmail] = useState("");
	const [telefon, setTelefon] = useState("");
	const [company, setCompany] = useState("");
	const [message, setMessage] = useState("");

	const isAnyEmpty = (values: string[]) =>
		values.some((value) => value.trim().length === 0);

	const buildMessage = (
		name: string,
		email: string,
		telefon: string,
		company: string,
		message: string
	) => {
		let final = "";

		final += `Name: ${name}\n`;
		final += `Email: ${email}\n`;
		final += `Telefon: ${telefon}\n`;
		final += `Firma: ${company}\n`;
		final += `Nachricht: \n${message}`;

		return final;
	};

	const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();

		if (isAnyEmpty([name, email, telefon, company, message])) {
			DSGStore.update((state) => {
				state.messageModalOpen = true;
				state.messageModalTitle = "Bitte fülle alle Felder aus!";
				state.messageModalMessage =
					"Um die Nachricht versenden zu können, müssen alle Felder ausgefüllt sein.";
			});
			return;
		}

		setSending(true);

		// Askrella Notify, tested 23.04.2024
		const body = {
			subject: "DS Gesellschaft - Kontaktformular",
			message: buildMessage(name, email, telefon, company, message),
			routing: {
				organization_id: "a9ed19f9-05c8-4c3d-8adb-7ea9d79a7e80",
				user_id: "a9ed19f9-05c8-4c3d-8adb-7ea9d79a7e80",
				event_name: "askrella.notify.contact",
			},
		};

		fetch("https://notify.askrella.agency/v1/contact", {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify(body),
		})
			.then((response) => {
				if (response.status !== 200) {
					throw new Error();
				}

				setName("");
				setEmail("");
				setTelefon("");
				setCompany("");
				setMessage("");

				setSending(false);

				DSGStore.update((state) => {
					state.messageModalOpen = true;
					state.messageModalTitle = "Nachricht gesendet!";
					state.messageModalMessage =
						"Vielen Dank für Ihre Nachricht. Wir werden uns so schnell wie möglich bei Ihnen melden.";
				});
			})
			.catch((error) => {
				console.log(error);

				setSending(false);

				DSGStore.update((state) => {
					state.messageModalOpen = true;
					state.messageModalTitle = "Es ist ein Fehler aufgetreten!";
					state.messageModalMessage =
						"Leider ist beim Senden der Nachricht ein Fehler aufgetreten. Bitte versuchen Sie es später noch einmal.";
				});
			});
	};

	return (
		<div
			id="contact"
			className="relative flex flex-col bg-ds-gray-dark pl-12 pr-12 pt-12 text-white md:p-24 xl:px-44"
		>
			<h1 className="z-10 text-5xl md:text-6xl">
				Ihr Weg zu <span className="text-ds-yellow">uns</span>
			</h1>
			<p className="z-10 mt-5 text-xl uppercase tracking-[0.25em] opacity-20 lg:text-2xl">
				Kontaktieren Sie uns
			</p>
			<div className="2xl:px-18 mt-16 flex flex-col-reverse lg:flex-row">
				<div className="m-10 mb-14 flex flex-col items-center lg:mr-16">
					<img
						src="/assets/logo.png"
						alt="logo"
						className="h-[150px]"
					/>

					<div className="mt-4 text-center text-lg font-medium text-ds-yellow">
						Deutsch-Schweizerische <br />
						Immobiliengesellschaft mbH
					</div>

					<ContactInfo
						location="Schweiz"
						address={["Schlösslihalde 27", "6006 Luzern"]}
						person="Maik Hösel"
						phone="+41 78 4046011"
						email="maik.hoesel@ds-gesellschaft.ch"
					/>

					<ContactInfo
						location="Deutschland"
						address={["Altkötzschenbroda 55", "01445 Radebeul"]}
						person="Erik Hösel"
						phone="+49 162 9824319"
						email="erik.hoesel@ds-gesellschaft.ch"
					/>
				</div>
				<form
					onSubmit={onSubmit}
					className="z-10 flex grow flex-col md:m-10 md:max-w-[650px] lg:mt-10 lg:max-w-none"
				>
					<div className="grid grid-cols-1 grid-rows-2 gap-4 sm:grid-cols-2 md:gap-6 lg:gap-8 xl:mr-8 2xl:mr-20">
						<InputField
							value={name}
							placeHolder="Name"
							onChange={setName}
							type="text"
						/>
						<InputField
							value={telefon}
							placeHolder="Telefon"
							onChange={setTelefon}
							type="text"
						/>
						<InputField
							value={email}
							placeHolder="E-Mail"
							onChange={setEmail}
							type="email"
						/>
						<InputField
							value={company}
							placeHolder="Firma"
							onChange={setCompany}
							type="text"
						/>
					</div>
					<textarea
						value={message}
						onChange={(event) => setMessage(event.target.value)}
						placeholder="Ihre Nachricht"
						rows={8}
						className="no-scrollbar mt-4 min-h-[120px] w-full flex-1 rounded-xl border-none bg-ds-gray-light p-3 pl-4 transition duration-500 focus:border-ds-yellow focus:ring-ds-yellow"
					/>

					<button
						type="submit"
						className={
							"ml-auto mt-4 flex w-36 items-center justify-between rounded-2xl py-1 px-7 " +
							"bg-ds-yellowsend hover:scale-105 hover:bg-ds-orange " +
							"transition delay-100 ease-in-out"
						}
					>
						{sending ? "Wird gesendet..." : "Senden"}
						<ArrowRightIcon className="h-5 w-5" />
					</button>
				</form>
			</div>
			<div className="pointer-events-none absolute inset-0 mt-8 ml-24 hidden text-6xl text-[9rem] font-semibold text-white opacity-[0.07] md:block lg:ml-44 lg:text-[12rem]">
				<h2>Kontakt</h2>
			</div>
		</div>
	);
};

export default Contact;
